import {
  PropertyIdentification,
  RESOListing,
  addressToString,
  isDefined,
  orUndefined,
} from '@aloft/toolkit-shared';
import { Block, Flex, Icon, Tooltip } from '@aloft/toolkit-sui';
import { UseQueryResult } from '@tanstack/react-query';

import { useMemo, useState } from 'react';

import { ListingResult } from '../../../controller/hooks/listings/types.ts';
import { useStreetViewStaticUrl } from '../../../hooks/googleMaps/useStreetViewStaticUrl.ts';
import { DataSheet } from '../../../modules/referenceView/types.ts';
import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../../redux/features/referenceBarUISlice.ts';
import { selectBackupListingDataById } from '../../../redux/features/selectors/propertyDataSelectors/backupListingDataSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';

const getImageInfo = (
  photoUrl: string | undefined,
  imgUrl: string | undefined,
): { tooltip: string; dataSheet: DataSheet } => {
  if (imgUrl === photoUrl) {
    return { tooltip: 'View Photos', dataSheet: 'mls-images' };
  }

  return { tooltip: 'View Street View', dataSheet: 'mls-street-view' };
};

function getMlsPhotoUrl(
  listing: UseQueryResult<ListingResult>,
  backup: RESOListing | undefined,
): string | undefined {
  // Primary listing used for images
  if (listing.data?.status === 'found' && listing.data.listing.Media?.[0]?.MediaURL) {
    return listing.data.listing.Media?.[0]?.MediaURL;
  }

  // Use the backup listing for the image if it exists
  if (backup?.Media?.[0]?.MediaURL) {
    return backup.Media?.[0]?.MediaURL;
  }

  return undefined;
}

type ValuationTablePropertyHeaderImageProps = {
  hideActions: boolean;
  listing: UseQueryResult<ListingResult>;
  propertyId: string;
  propertyIdentification: PropertyIdentification;
};

export const ValuationTablePropertyHeaderImage = ({
  hideActions,
  listing,
  propertyId,
  propertyIdentification,
}: ValuationTablePropertyHeaderImageProps) => {
  const { address, coordinates } = propertyIdentification;

  const dispatch = useAppDispatch();
  const backupListing = useAppSelector((state) => selectBackupListingDataById(state, propertyId));

  const photoUrl = getMlsPhotoUrl(listing, backupListing);
  const streetViewStaticUrl = useStreetViewStaticUrl(
    orUndefined(addressToString)(address),
    { lat: coordinates?.latitude, lng: coordinates?.longitude },
    !photoUrl,
  );
  const imgUrls = useMemo(
    () => [photoUrl, streetViewStaticUrl].filter(isDefined),
    [photoUrl, streetViewStaticUrl],
  );
  const [imgUrlIndex, setImgUrlIndex] = useState<number>(0);
  const imgUrl = imgUrls[imgUrlIndex];

  const handleImageClick = useMemo(
    () => () => {
      if (hideActions) return;
      const { dataSheet } = getImageInfo(photoUrl, imgUrl);
      dispatch(setSelectedDataSheet(dataSheet));
      dispatch(setReferenceViewOpen(true));
    },
    [dispatch, hideActions, imgUrl, photoUrl],
  );

  const { tooltip } = getImageInfo(photoUrl, imgUrl);

  return (
    <Tooltip openDelay={500} label={tooltip} isDisabled={hideActions}>
      <Block
        height="110px"
        width="99px"
        minWidth="81px"
        onClick={handleImageClick}
        cursor={hideActions ? 'default' : 'pointer'}
      >
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100%"
          width="100%"
          backgroundColor="strokeDefault"
        >
          {imgUrl ? (
            <img
              alt="Property"
              height="100%"
              width="100%"
              src={imgUrl}
              style={{ height: '100%', width: '100%', objectFit: 'cover' }}
              onError={() => {
                if (imgUrlIndex === imgUrls.length - 1) {
                  return;
                }
                setImgUrlIndex((prev) => prev + 1);
              }}
            />
          ) : (
            <Icon icon="home-2" size="lg" color="strokeActive" />
          )}
        </Flex>
      </Block>
    </Tooltip>
  );
};
